/**
 * Custom/X10000 Contract Addresses
 *
 * Contract addresses specific to x10000 trading mode and API adapters
 * Updated for BabyDriver deployment on Arbitrum Sepolia
 */

import { zeroAddress } from "viem";
import { ARBITRUM, ARBITRUM_SEPOLIA } from "../chains";

/**
 * X10000 specific contract addresses by chain ID
 *
 * These are used for deposit/withdrawal operations in x10000 mode.
 * The configuration automatically switches based on the chainId:
 * - ARBITRUM_SEPOLIA (421614): BabyDriver Testnet addresses
 * - ARBITRUM (42161): Mainnet/production addresses
 *
 * Use the getter functions (getX10000UsdtAddress, getX10000ZtdxVaultAddress, getReferralRebateAddress)
 * to retrieve addresses for a specific chainId.
 */
export const X10000_CONTRACTS = {
  [ARBITRUM_SEPOLIA]: {
    // BabyDriver USDT token address on Arbitrum Sepolia testnet
    USDT: "0xb8486A01a7EaAB9e34553E7B323C9C1FAE1fA7B6",
    // BabyDriverVault contract address on Arbitrum Sepolia
    ZTDX_VAULT: "0xfDB61D1eA5b9d7CFC9bF7932a09705B2Df903c7C",
    // BabyDriverRebate contract address on Arbitrum Sepolia testnet
    REFERRAL_REBATE: "0x2016D692c2B1Fc8d5866C3b8E14bcB7D495a937d",
    // BabyDriverEarn contract address on Arbitrum Sepolia testnet
    EARN: "0x3DC83FaBb8Ec508ae64EF2ed14b69D90f87d0165",
  },
  [ARBITRUM]: {
    // USDT token address on Arbitrum mainnet
    // Used for deposits in x10000 mode
    USDT: "0x6A2D98c29F8938312dD13C038585a6fe67CA2B8B",
    // ZTDXVault contract address on Arbitrum mainnet
    // Used for same-chain deposits in x10000 mode
    ZTDX_VAULT: "0x5d2efcbdC2dD4b9Ff06Ea396F62878Ef982377c2",
    // ReferralRebate contract address on Arbitrum mainnet (production)
    REFERRAL_REBATE: "0x5Af4Faf59f6EfCE8f70110222750Ca73f5552433",
    // AXBladeEarn contract address on Arbitrum mainnet (TBD)
    EARN: "0xA40b4b726980C02F6558fcA5C0a52FaA2E5A0864",
  },
} as const;

/**
 * Market symbol to contract address mapping
 * Used by API adapters (orderAdapter, tradeAdapter, positionAdapter)
 *
 * Maps market symbols (e.g., "BTC-USD", "ETHUSDT") to their contract addresses
 */
export const MARKET_SYMBOL_TO_ADDRESS: Record<number, Record<string, string>> = {
  [ARBITRUM_SEPOLIA]: {
    "BTC-USD": "0xBb532Ab4923C23c2bfA455151B14fec177a34C0D",
    "ETH-USD": "0x482Df3D320C964808579b585a8AC7Dd5D144eFaF",
    "ZTDX-USD": "0x756be641d97c796bd13856c76830f274fb0ac857",
    "SOL-USD": "0x09400D9DB990D5ed3f35D7be61DfAEB900Af03C9",
    // Also support API format (ETHUSDT -> ETH-USD)
    "BTCUSDT": "0xBb532Ab4923C23c2bfA455151B14fec177a34C0D",
    "ETHUSDT": "0x482Df3D320C964808579b585a8AC7Dd5D144eFaF",
    "ZTDXUSDT": "0x756be641d97c796bd13856c76830f274fb0ac857",
    "SOLUSDT": "0x09400D9DB990D5ed3f35D7be61DfAEB900Af03C9",
  },
  [ARBITRUM]: {
    // Mainnet market addresses - Use SDK standard addresses for compatibility
    "BTC-USD": "0x47c031236e19d024b42f8AE6780E44A573170703",
    "ETH-USD": "0x70d95587d40A2caf56bd97485aB3Eec10Bee6336",
    "SOL-USD": "0x09400D9DB990D5ed3f35D7be61DfAEB900Af03C9",
    // Also support API format (ETHUSDT -> ETH-USD)
    "BTCUSDT": "0x47c031236e19d024b42f8AE6780E44A573170703",
    "ETHUSDT": "0x70d95587d40A2caf56bd97485aB3Eec10Bee6336",
    "SOLUSDT": "0x09400D9DB990D5ed3f35D7be61DfAEB900Af03C9",
  },
} as const;

/**
 * Default collateral token addresses by chain
 * Used by API adapters for order/position/trade conversion
 */
export const DEFAULT_COLLATERAL_ADDRESS: Record<number, string> = {
  [ARBITRUM_SEPOLIA]: "0xb8486A01a7EaAB9e34553E7B323C9C1FAE1fA7B6", // BabyDriver USDT on Arbitrum Sepolia
  [ARBITRUM]: "0x6A2D98c29F8938312dD13C038585a6fe67CA2B8B", // USDT on Arbitrum mainnet (x10000 mode)
} as const;

/**
 * Get USDT token address for a given chain ID in x10000 mode
 */
export function getX10000UsdtAddress(chainId: number): string | undefined {
  return X10000_CONTRACTS[chainId as keyof typeof X10000_CONTRACTS]?.USDT;
}

/**
 * Check if a token address is the USDT address for x10000 mode on the given chain
 */
export function isX10000UsdtAddress(chainId: number, tokenAddress: string): boolean {
  const usdtAddress = getX10000UsdtAddress(chainId);
  return usdtAddress?.toLowerCase() === tokenAddress.toLowerCase();
}

/**
 * Get ZTDXVault contract address for a given chain ID in x10000 mode
 */
export function getX10000ZtdxVaultAddress(chainId: number): string | undefined {
  return X10000_CONTRACTS[chainId as keyof typeof X10000_CONTRACTS]?.ZTDX_VAULT;
}

/**
 * Get market address from symbol for a given chain ID
 * Used by API adapters to convert API symbols to contract addresses
 */
export function getMarketAddressFromSymbol(chainId: number, symbol: string): string | undefined {
  return MARKET_SYMBOL_TO_ADDRESS[chainId]?.[symbol];
}

/**
 * Get symbol from market address for a given chain ID
 * Reverse lookup of getMarketAddressFromSymbol
 */
export function getSymbolFromMarketAddress(chainId: number, marketAddress: string): string | undefined {
  const mapping = MARKET_SYMBOL_TO_ADDRESS[chainId];
  if (!mapping) return undefined;

  for (const [symbol, address] of Object.entries(mapping)) {
    if (address.toLowerCase() === marketAddress.toLowerCase()) {
      return symbol;
    }
  }
  return undefined;
}

/**
 * Get default collateral token address for a given chain ID
 * Used by API adapters for order/position/trade conversion
 */
export function getDefaultCollateralAddress(chainId: number): string | undefined {
  return DEFAULT_COLLATERAL_ADDRESS[chainId];
}

/**
 * Get ReferralRebate contract address for a given chain ID
 */
export function getReferralRebateAddress(chainId: number): string | undefined {
  return X10000_CONTRACTS[chainId as keyof typeof X10000_CONTRACTS]?.REFERRAL_REBATE;
}

/**
 * Get Earn contract address for a given chain ID
 */
export function getEarnContractAddress(chainId: number): string | undefined {
  return X10000_CONTRACTS[chainId as keyof typeof X10000_CONTRACTS]?.EARN;
}
